import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { AuditMainTabs } from "@/components/admin/audit/AuditMainTabs";

export default function ReportsPage() {
  const { hasGlobalRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasGlobalRole('portal_admin') && !hasGlobalRole('license_manager') && !hasGlobalRole('auditor')) {
      navigate('/admin');
    }
  }, [hasGlobalRole, navigate]);

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['admin-dashboard-reports'],
    queryFn: async () => {
      // Fetch companies count
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('status')
        .order('created_at', { ascending: false });

      if (companiesError) throw companiesError;

      // Fetch active modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('company_modules')
        .select(`
          status,
          seats,
          modules (
            name,
            code
          )
        `);

      if (modulesError) throw modulesError;

      // Fetch payments summary
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('company_payments')
        .select('amount_cents, status, created_at');

      if (paymentsError) throw paymentsError;

      // Calculate metrics
      const activeCompanies = companiesData?.filter(c => c.status === 'active').length || 0;
      const totalCompanies = companiesData?.length || 0;
      
      const activeModules = modulesData?.filter(m => m.status === 'active').length || 0;
      const totalSeats = modulesData?.reduce((acc, m) => acc + (m.seats || 0), 0) || 0;

      const totalRevenue = paymentsData?.reduce((acc, p) => acc + (p.amount_cents || 0), 0) || 0;
      const paidPayments = paymentsData?.filter(p => p.status === 'paid').length || 0;

      // Module usage data for charts
      const moduleUsage = modulesData?.reduce((acc: any, module) => {
        const moduleName = module.modules?.name || 'Unknown';
        if (!acc[moduleName]) {
          acc[moduleName] = { name: moduleName, count: 0, seats: 0 };
        }
        acc[moduleName].count++;
        acc[moduleName].seats += module.seats || 0;
        return acc;
      }, {});

      const moduleChartData = Object.values(moduleUsage || {});

      return {
        companies: {
          active: activeCompanies,
          total: totalCompanies,
          inactive: totalCompanies - activeCompanies
        },
        modules: {
          active: activeModules,
          totalSeats: totalSeats,
          chartData: moduleChartData
        },
        revenue: {
          total: totalRevenue,
          paidCount: paidPayments,
          totalPayments: paymentsData?.length || 0
        }
      };
    },
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-6">Relatórios e Analytics</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded mb-2" />
                <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const stats = dashboardData;

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Relatórios e Analytics</h1>
        <p className="text-muted-foreground">
          Visão geral de métricas e auditoria do PortalONE Suite
        </p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard Geral</TabsTrigger>
          <TabsTrigger value="audit">Auditoria Administrativa</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Empresas Ativas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.companies.active}</div>
            <p className="text-xs text-muted-foreground">
              de {stats?.companies.total} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Módulos Ativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.modules.active}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.modules.totalSeats} seats total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Receita Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {((stats?.revenue.total || 0) / 100).toLocaleString('pt-BR', { 
                minimumFractionDigits: 2 
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.revenue.paidCount} pagamentos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-green-50">
                Operacional
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Todos os sistemas online
            </p>
          </CardContent>
        </Card>
      </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Distribuição de Módulos</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.modules.chartData || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status das Empresas</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Ativas', value: stats?.companies.active || 0 },
                    { name: 'Inativas', value: stats?.companies.inactive || 0 },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { name: 'Ativas', value: stats?.companies.active || 0 },
                    { name: 'Inativas', value: stats?.companies.inactive || 0 },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="space-y-6">
          <AuditMainTabs />
        </TabsContent>
      </Tabs>
    </div>
  );
}